import { zodResolver } from '@hookform/resolvers/zod';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { HardRule, RuleCondition, SoftRule } from '../../types';

const ruleConditionSchema = z.object({
  field: z.string().min(1, 'Field is required'),
  operator: z.enum([
    'equals',
    'not_equals',
    'less_than',
    'greater_than',
    'less_than_or_equal',
    'greater_than_or_equal',
    'contains',
    'matches',
    'unique_per_timeslot',
    'must_match',
    'is_consecutive',
    'same_as',
    'between',
    'minimize_gaps',
    'minimize_working_days',
    'spread_evenly',
    'consistent_weekly',
    'is_free',
  ]),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]).nullable(),
});

const ruleSchema = z.object({
  name: z.string().min(1, 'Rule name is required'),
  description: z.string().min(1, 'Description is required'),
  category: z.enum(['teacher', 'room', 'student', 'curriculum', 'timing', 'collaboration', 'scheduling']),
  conditions: z.array(ruleConditionSchema).min(1, 'At least one condition is required'),
  isActive: z.boolean(),
  // Specific to soft rules
  weight: z.coerce.number().min(1).max(10).optional(),
  // Specific to hard rules
  priority: z.coerce.number().min(1).max(5).optional(),
});

type RuleFormData = z.infer<typeof ruleSchema>;

interface RuleBuilderProps {
  rule?: HardRule | SoftRule;
  ruleType: 'hard' | 'soft';
  onSubmit: (data: HardRule | SoftRule) => void;
  onCancel: () => void;
}

const operators = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than_or_equal', label: 'Less Than or Equal' },
  { value: 'greater_than_or_equal', label: 'Greater Than or Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'matches', label: 'Matches' },
  { value: 'unique_per_timeslot', label: 'Unique Per Timeslot' },
  { value: 'must_match', label: 'Must Match' },
  { value: 'is_consecutive', label: 'Is Consecutive' },
  { value: 'same_as', label: 'Same As' },
  { value: 'between', label: 'Between' },
  { value: 'minimize_gaps', label: 'Minimize Gaps' },
  { value: 'minimize_working_days', label: 'Minimize Working Days' },
  { value: 'spread_evenly', label: 'Spread Evenly' },
  { value: 'consistent_weekly', label: 'Consistent Weekly' },
  { value: 'is_free', label: 'Is Free' },
];

const categories = [
  { value: 'teacher', label: 'Teacher' },
  { value: 'room', label: 'Room' },
  { value: 'student', label: 'Student' },
  { value: 'curriculum', label: 'Curriculum' },
  { value: 'timing', label: 'Timing' },
  { value: 'collaboration', label: 'Collaboration' },
  { value: 'scheduling', label: 'Scheduling' },
];

export const RuleBuilder: React.FC<RuleBuilderProps> = ({ rule, ruleType, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RuleFormData>({
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      name: rule?.name || '',
      description: rule?.description || '',
      category: rule?.category || 'teacher',
      conditions: rule?.conditions || [{ field: '', operator: 'equals', value: null }],
      isActive: rule?.isActive ?? true,
      weight: 'weight' in rule ? rule.weight : 5,
      priority: 'priority' in rule ? rule.priority : 1,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditions',
  });

  const handleFormSubmit = (data: RuleFormData) => {
    const baseRule = {
      id: rule?.id || `${ruleType === 'hard' ? 'hr' : 'sr'}-${Date.now()}`,
      name: data.name,
      description: data.description,
      category: data.category,
      conditions: data.conditions as RuleCondition[],
      isActive: data.isActive,
      createdAt: rule?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    if (ruleType === 'soft') {
      onSubmit({
        ...baseRule,
        weight: data.weight || 5,
      } as SoftRule);
    } else {
      onSubmit({
        ...baseRule,
        priority: data.priority || 1,
      } as HardRule);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Rule Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={2}
                error={!!errors.description}
                helperText={errors.description?.message}
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select {...field} label="Category *">
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>

        {ruleType === 'soft' ? (
          <Grid item xs={12} sm={6}>
            <Controller
              name="weight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Weight (1-10)"
                  type="number"
                  fullWidth
                  error={!!errors.weight}
                  helperText={errors.weight?.message || '1 = Low importance, 10 = High importance'}
                  required
                />
              )}
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={6}>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Priority (1-5)"
                  type="number"
                  fullWidth
                  error={!!errors.priority}
                  helperText={errors.priority?.message || '1 = Critical, 5 = Lower priority'}
                  required
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <strong>Conditions</strong>
            <Button
              startIcon={<AddIcon />}
              onClick={() => append({ field: '', operator: 'equals', value: null })}
              size="small"
            >
              Add Condition
            </Button>
          </Box>
        </Grid>

        {fields.map((field, index) => (
          <Grid item xs={12} key={field.id}>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`conditions.${index}.field`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Field"
                        fullWidth
                        error={!!errors.conditions?.[index]?.field}
                        helperText={errors.conditions?.[index]?.field?.message || 'e.g., teacher, room.capacity'}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`conditions.${index}.operator`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.conditions?.[index]?.operator}>
                        <InputLabel>Operator *</InputLabel>
                        <Select {...field} label="Operator *">
                          {operators.map((op) => (
                            <MenuItem key={op.value} value={op.value}>
                              {op.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`conditions.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        label="Value"
                        fullWidth
                        helperText="Optional for some operators"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => remove(index)} color="error" disabled={fields.length === 1}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {rule ? 'Update' : 'Create'} {ruleType === 'hard' ? 'Hard' : 'Soft'} Rule
        </Button>
      </Box>
    </form>
  );
};
